import { useEffect, useState } from "react";
import { listRowIssues, type RowIssueRow } from "./actions";
import { downloadCsv } from "./csv";

// The products the feed guard KEPT but flagged (red: a critical problem, orange: a soft one), or the rows it could not store at all
// (rejected: no identity or a damaged field count). Look at them here first; export is a download only and sends nothing to anyone.

type Obj = Record<string, unknown>;
const str = (o: Obj | null | undefined, k: string) => (o && typeof o[k] === "string" ? (o[k] as string) : "");
const reasons = (r: RowIssueRow) => (Array.isArray(r.issue.reasons) ? (r.issue.reasons as string[]) : []);
const csvCell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const TITLE = { red: "Feed problems (critical)", orange: "Feed problems (soft)", rejected: "Rows the ingest could not store" } as const;

export function RowIssuesPanel({ severity }: { severity: "red" | "orange" | "rejected" }) {
  const [rows, setRows] = useState<RowIssueRow[]>([]);
  const [cursor, setCursor] = useState<unknown>(undefined);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (c?: unknown, append = false) => {
    setLoading(true);
    const r = await listRowIssues(severity, c);
    setLoading(false);
    if (!r.ok) { setError(r.error); return; }
    setRows((prev) => (append ? [...prev, ...r.data.rows] : r.data.rows));
    setCursor(r.data.nextCursor ?? undefined);
    setTotal(r.data.total);
  };
  useEffect(() => { load(); }, [severity]);

  const exportAll = async () => {
    let all: RowIssueRow[] = [], c: unknown = undefined;
    for (;;) {
      const r = await listRowIssues(severity, c, 500);
      if (!r.ok) { setError(r.error); return; }
      all = all.concat(r.data.rows);
      if (!r.data.nextCursor) break;
      c = r.data.nextCursor;
    }
    const head = ["feed", "sku", "name", "manufacturer", "severity", "problems", "file", "line", "raw line", "run date"];
    const lines = all.map((r) => [str(r.issue, "source_id"), str(r.product, "sku") || str(r.issue, "key"), str(r.product, "name"),
      str(r.product, "manufacturer_name"), str(r.issue, "severity"), reasons(r).join("; "), str(r.issue, "file"), r.issue.line ?? "",
      str(r.issue, "raw"), str(r.issue, "run_date")]);
    downloadCsv(`hec-feed-guard-${severity}.csv`, [head, ...lines].map((l) => l.map(csvCell).join(",")).join("\r\n"));
  };

  return (
    <div className="sheet-page">
      <div className="sheet-top">
        <div className="sheet-title">
          <h1>{TITLE[severity]}</h1>
          <span className="meta">
            {loading && rows.length === 0 ? "Loading…" : `${(total ?? rows.length).toLocaleString()} item${total === 1 ? "" : "s"}`}.
            {severity === "rejected" ? " These rows were not stored; the raw line is kept so nothing is lost." : " The products are kept; only the bad value is left out."}
          </span>
        </div>
        {error && <div className="panel error sheet-error">{error}</div>}
        <div className="sheet-actions">
            <button type="button" className="sheet-export" disabled={!rows.length} onClick={exportAll}>Export all as CSV</button>
        </div>
      </div>
      <div className="sheet-scroll">
        <table className="manual-products sheet-table feed-table">
          <thead><tr><th>Feed</th><th>SKU / key</th><th>Name</th><th>Manufacturer</th><th>Problems</th><th>Where in the file</th>{severity === "rejected" && <th>Raw line</th>}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.docId}>
                <td>{str(r.issue, "source_id")}</td>
                <td className="mono">{str(r.product, "sku") || str(r.issue, "key") || "—"}</td>
                <td className="wrap">{str(r.product, "name")}</td>
                <td className="wrap">{str(r.product, "manufacturer_name")}</td>
                <td className="wrap">{reasons(r).join("; ")}</td>
                <td className="mono">{str(r.issue, "file")} line {String(r.issue.line ?? "")}</td>
                {severity === "rejected" && <td className="mono wrap">{str(r.issue, "raw").slice(0, 200)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !loading && !error && <p className="meta" style={{ padding: 16 }}>Nothing flagged in this environment.</p>}
        {cursor ? <button type="button" style={{ margin: 12 }} disabled={loading} onClick={() => load(cursor, true)}>{loading ? "Loading…" : "Load more"}</button> : null}
      </div>
    </div>
  );
}
